"use client";

import './index.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useEffect, useId } from 'react';
import Plus from '../../icons/component-icons/plus/index.js';
import Minus from '../../icons/component-icons/minus/index.js';
import '../../buttons/button/index.js';
import { validateSurface } from '../../globals.js';
import calculateRem from '../../utilities/calculate-rem/index.js';
import ScreenReaderText from '../../utilities/screen-reader-text/index.js';
import '../../utilities/use-viewport/index.js';
import '../../buttons/text-link/index.js';
import '../../icons/component-icons/left-caret-bold/index.js';
import '../../icons/component-icons/right-caret-bold/index.js';
import { ButtonIcon } from '../../buttons/button-icon/index.js';
import { HelperText } from '../../form-elements/helper-text/index.js';
import { StatusText } from '../../form-elements/status-text/index.js';
import { Label } from '../../form-elements/label/index.js';
import styles from './input-stepper.module.scss.js';

const _calculateWidth = (width) => {
    if (typeof width === 'string')
        return width;
    return calculateRem(width);
};
/**
 * @component InputStepper
 * @package InputStepper
 * @document ../_docs/code/InputStepper.editor.tsx
 * @document ../_docs/code/InputStepper.snippet.tsx
 * @document ../_docs/code/InputStepper.import.tsx
 */
const InputStepper = forwardRef((_a, ref) => {
    var { ariaLabel, id, defaultValue = 0, disabled, error, errorText, helperText, helperTextPosition = 'bottom', label, minValue = 0, maxValue = 99, onChange, required, readOnly, surface: surfaceProp = 'lightPrimary', tooltip, trailingText, controlWidth = 'auto', width = '100%', size = 'large', addButton, subtractButton, containerBackground = 'transparent' } = _a, rest = __rest(_a, ["ariaLabel", "id", "defaultValue", "disabled", "error", "errorText", "helperText", "helperTextPosition", "label", "minValue", "maxValue", "onChange", "required", "readOnly", "surface", "tooltip", "trailingText", "controlWidth", "width", "size", "addButton", "subtractButton", "containerBackground"]);
    // if maxValue is set greater than 99, default it to 99
    const _maxVal = Number(maxValue) > 99 ? 99 : Number(maxValue);
    const _minVal = Number(minValue) > 98 ? 98 : Number(minValue);
    const alertTimer = useRef(null);
    const updatedTimer = useRef(null);
    // initialise 'lineNumber' state variable and a function 'setLineNumber' to update it
    const [lineNumber, setLineNumber] = useState(Number(defaultValue));
    const [alertScreenReader, setAlertScreenReader] = useState('');
    const [updatedScreenReader, setUpdatedScreenReader] = useState(false);
    // call setLineNumber function whenever there is change in value and update lineNumber state value
    useEffect(() => setLineNumber(Number(defaultValue)), [defaultValue]);
    const _useId = useId();
    const inputId = id || _useId;
    const stepperValue = trailingText
        ? `${lineNumber}${trailingText}`
        : lineNumber;
    const surface = validateSurface(surfaceProp);
    // switch button focus to add button if stepper value reaches minvalue
    const handleFocusOnMin = (e) => {
        if (e.detail === 0 && lineNumber - 1 === _minVal) {
            const incBtn = document.getElementById(`add-${inputId}`);
            setTimeout(() => {
                incBtn === null || incBtn === void 0 ? void 0 : incBtn.focus();
            }, 100);
        }
    };
    // switch button focus to subtract button if stepper value reaches maxvalue
    const handleFocusOnMax = (e) => {
        if (e.detail === 0 && lineNumber + 1 === _maxVal) {
            const decBtn = document.getElementById(`subtract-${inputId}`);
            setTimeout(() => {
                decBtn === null || decBtn === void 0 ? void 0 : decBtn.focus();
            }, 100);
        }
    };
    const _onIncrease = (e) => {
        const _lineNumber = Number(lineNumber);
        // if _maxVal is defined and if lineNumber is greater than or equal to _maxVal return
        /* istanbul ignore next -- @preserve */
        if (_maxVal !== undefined && _lineNumber >= _maxVal) {
            return;
        }
        // if defaultValue <= minValue, consider minValue as defaultValue
        if (Number(defaultValue) < _minVal && _lineNumber < _minVal) {
            setLineNumber(_minVal);
        }
        else {
            // add lineNumber state value by 1 onClick of plus buttonIcon
            setLineNumber(_lineNumber + 1);
            setUpdatedScreenReader(_lineNumber + 1 !== _maxVal);
            setAlertScreenReader(_lineNumber + 1 === _maxVal
                ? `You have reached maximum value ${_maxVal}`
                : '');
        }
        if (onChange) {
            onChange(_lineNumber + 1);
        }
        handleFocusOnMax(e);
    };
    const _onDecrease = (e) => {
        const _lineNumber = Number(lineNumber);
        /* istanbul ignore next -- @preserve */
        if (_minVal !== undefined && _lineNumber <= _minVal) {
            return;
        }
        // if lineNumber is defined and is greater than 1 and min value
        /* istanbul ignore else @preserve */
        if (_lineNumber > 0 && _minVal !== undefined && _lineNumber > _minVal) {
            // if defaultValue <= maxValue, consider maxValue as defaultValue
            if (Number(defaultValue) > _maxVal && _lineNumber > _maxVal) {
                setLineNumber(_maxVal);
            }
            else {
                setUpdatedScreenReader(_lineNumber - 1 !== _minVal);
                // subtract lineNumber state value by 1 onClick of minus buttonIcon
                setAlertScreenReader(_lineNumber - 1 === _minVal
                    ? `You have reached minimum value ${_minVal}`
                    : '');
                setLineNumber(_lineNumber - 1);
            }
            if (onChange) {
                onChange(_lineNumber - 1);
            }
            handleFocusOnMin(e);
        }
    };
    // remove alert after alert text got announced
    useEffect(() => {
        if (alertTimer.current) {
            clearTimeout(alertTimer.current);
        }
        if (alertScreenReader !== '') {
            alertTimer.current = setTimeout(() => {
                setAlertScreenReader('');
            }, 100);
        }
    }, [alertScreenReader]);
    // remove screender text updated got announced
    useEffect(() => {
        if (updatedTimer.current) {
            clearTimeout(updatedTimer.current);
        }
        if (updatedScreenReader) {
            updatedTimer.current = setTimeout(() => {
                setUpdatedScreenReader(false);
            }, 100);
        }
    }, [updatedScreenReader]);
    const disableSubtract = disabled || readOnly || _minVal >= lineNumber;
    const disableAdd = disabled || readOnly || _maxVal <= lineNumber;
    const _getAriaLabel = (key) => {
        const { subtract, add, input } = ariaLabel || {};
        switch (key) {
            case 'subtract':
                return `${subtract || `Subtract ${label || ''}`.trim()}${disableSubtract ? ' in disabled state as the value is at its minimum' : ''}`;
            case 'add':
                return `${add || `Add ${label || ''}`.trim()}${disableAdd ? ' in disabled state as the value is at its maximum' : ''}`;
            case 'input':
                return input || `${label || ''} updated to`.trim();
        }
    };
    const filledbackgroundClass = () => (containerBackground === 'filled' && !error && !readOnly) ||
        (containerBackground === 'filled' && disabled)
        ? `bg-${containerBackground}`
        : '';
    const flexWrapperStyles = [styles.flexWrapper];
    const controlStyles = [
        styles.control,
        styles[surface.toLowerCase()],
        styles[filledbackgroundClass()],
        styles[containerBackground === 'filled' && readOnly ? `bg-filled-readonly` : ''],
    ];
    const contentWrapperStyles = [
        styles.contentWrapper,
        styles[surface.toLowerCase()],
        styles[filledbackgroundClass()],
    ];
    const buttonIconWrapper = [
        styles.buttonIconWrapper,
        styles[surface.toLowerCase()],
        styles[filledbackgroundClass()],
    ];
    const labelContainer = [styles.labelContainer];
    if (disabled) {
        controlStyles.push(styles.disabled);
        contentWrapperStyles.push(styles.disabled);
    }
    if (readOnly) {
        controlStyles.push(styles.readOnly);
        contentWrapperStyles.push(styles.readOnly);
    }
    if (error && !disabled && !readOnly) {
        controlStyles.push(styles.error);
        contentWrapperStyles.push(styles.error);
        buttonIconWrapper.push(styles.error);
    }
    if (size === 'small') {
        controlStyles.push(styles.small);
        contentWrapperStyles.push(styles.small);
        buttonIconWrapper.push(styles.small);
    }
    if (helperTextPosition === 'right') {
        labelContainer.push(styles.helperTextRight);
        flexWrapperStyles.push(styles.right);
    }
    const _renderHelperText = () => (jsx(HelperText, { helperText: helperText, helperTextPosition: helperTextPosition, disabled: disabled, surface: surface }));
    const subtractButtonVisibility = Number(lineNumber) <= Number(minValue) || (readOnly && !disabled)
        ? 'hidden'
        : 'visible';
    const addButtonVisibility = Number(lineNumber) >= Number(maxValue) || (readOnly && !disabled)
        ? 'hidden'
        : 'visible';
    return (jsxs("div", { className: styles.inputStepper, style: {
            width: width ? _calculateWidth(width) : undefined,
        }, children: [label && (jsx("div", { className: labelContainer.join(' '), children: jsx(Label, { required: required, disabled: disabled, label: label, tooltip: tooltip, "aria-label": label, surface: surface, htmlFor: inputId }) })), jsxs("div", { className: flexWrapperStyles.join(' '), children: [jsxs("div", { className: styles.controlContainer, children: [jsxs("div", { className: controlStyles.join(' '), style: {
                                    width: controlWidth === 'auto'
                                        ? 'fit-content'
                                        : _calculateWidth(controlWidth),
                                }, children: [jsx("input", Object.assign({ id: inputId, ref: ref }, rest, { type: "hidden", value: lineNumber })), jsx("span", { className: buttonIconWrapper.join(' '), style: {
                                            visibility: subtractButtonVisibility,
                                        }, onMouseDown: (e) => {
                                            handleFocusOnMin(e);
                                        }, children: jsx(ButtonIcon, Object.assign({}, subtractButton, { id: `subtract-${inputId}`, kind: "ghost", size: size === 'small' ? '24' : 'small', focusBorderPosition: "inside", disabled: disableSubtract, ariaLabel: _getAriaLabel('subtract'), "aria-live": "off", onClick: (e) => {
                                                _onDecrease(e);
                                            }, surface: surface, iconOffset: {
                                                x: 0,
                                                y: 0,
                                            }, renderIcon: (iconprops) => (jsx(Minus, { surface: surface, iconprops,
                                                size: size === 'small' ? '16px' : '20px',
                                                ariaHidden: true })) })) }), jsx("div", { className: contentWrapperStyles.join(' '), children: stepperValue }), jsx(ScreenReaderText, { "aria-relevant": "text", "aria-live": "assertive", children: updatedScreenReader && (jsxs(Fragment, { children: [_getAriaLabel('input'), " ", stepperValue] })) }), alertScreenReader && (jsxs(ScreenReaderText, { role: "alert", children: [_getAriaLabel('input'), " ", stepperValue, alertScreenReader] })), jsx("span", { className: buttonIconWrapper.join(' '), style: {
                                            visibility: addButtonVisibility,
                                        }, onMouseDown: (e) => {
                                            handleFocusOnMax(e);
                                        }, children: jsx(ButtonIcon, Object.assign({}, addButton, { id: `add-${inputId}`, kind: "ghost", size: size === 'small' ? '24' : 'small', focusBorderPosition: "inside", disabled: disableAdd, ariaLabel: _getAriaLabel('add'), "aria-live": "off", iconOffset: {
                                                x: 0,
                                                y: 0,
                                            }, onClick: (e) => {
                                                _onIncrease(e);
                                            }, surface: surface, renderIcon: (iconprops) => (jsx(Plus, { surface: surface, iconprops,
                                                size: size === 'small' ? '16px' : '20px',
                                                ariaHidden: true })) })) })] }), error && errorText && !disabled && !readOnly && (jsx(StatusText, { errorText: errorText, error: error, surface: surface })), helperText &&
                                helperTextPosition === 'bottom' &&
                                _renderHelperText()] }), helperText && helperTextPosition === 'right' && _renderHelperText()] })] }));
});

export { InputStepper, InputStepper as default };
